import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Sandbox } from '../Sandbox';
import { PuzzleService, formatPuzzleDisplayTitle, type Puzzle, type SolutionMove } from '../../services/puzzleService';
import { AdminApiService, type IgoPuzzleVerifyResult, type ConstructedPuzzleCandidate, type PuzzleConstructProgress } from '../../services/adminApiService';
import type { Cell } from '../../game/types';
import { INITIAL_BOARD } from '../../game/types';
import { copyBoard, hasLegalMoves } from '../../game/logic/board';
import { checkForYugos, processYugos } from '../../game/logic/yugos';
import { checkForIgo } from '../../game/logic/igo';
import './AdminPuzzleCreator.css';

const squareName = (row: number, col: number) => `${String.fromCharCode(97 + col)}${8 - row}`;

const describeSquares = (squares: Array<{ row: number; col: number }>) =>
  squares.map((s) => squareName(s.row, s.col)).join(', ');

/**
 * Format a solution line as a stacked game score (one numbered pair per line):
 * white to move → "1. d6 b6\n2. d7 d5"
 * black to move → "1... d6\n2. b6 d7"
 */
const formatSolutionNotation = (
  line: Array<{ row: number; col: number; player: 'white' | 'black' }>
) => {
  if (!line.length) return '';
  const parts: string[] = [];
  let i = 0;
  let moveNumber = 1;

  if (line[0].player === 'black') {
    parts.push(`1... ${squareName(line[0].row, line[0].col)}`);
    i = 1;
    moveNumber = 2;
  }

  while (i < line.length) {
    const first = line[i];
    if (first.player === 'white') {
      const whiteSq = squareName(first.row, first.col);
      i += 1;
      if (i < line.length && line[i].player === 'black') {
        parts.push(`${moveNumber}. ${whiteSq} ${squareName(line[i].row, line[i].col)}`);
        i += 1;
      } else {
        parts.push(`${moveNumber}. ${whiteSq}`);
      }
      moveNumber += 1;
    } else {
      parts.push(`${moveNumber}... ${squareName(first.row, first.col)}`);
      i += 1;
      moveNumber += 1;
    }
  }

  return parts.join('\n');
};

/**
 * Identifies a position, so a verification result can be discarded the moment
 * the board is edited. Showing a stale verdict against a changed board is how a
 * broken puzzle would get published.
 */
const positionSignature = (board: (Cell | null)[][] | null, startingPlayer: 'white' | 'black') => {
  if (!board) return '';
  let signature = `${startingPlayer}|`;
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const cell = board[row][col];
      signature += cell ? (cell.color === 'white' ? (cell.isYugo ? 'W' : 'w') : cell.isYugo ? 'B' : 'b') : '.';
    }
  }
  return signature;
};

/** One numbered pair per line, as the game replay's copy button writes it. */
const MOVE_LINE = /^\s*(\d+)\.\s*([a-h][1-8])(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*([a-h][1-8])?(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*$/i;

const parseSquare = (notation: string): { row: number; col: number } | null => {
  if (!notation || notation.length !== 2) return null;
  // The replay writes uppercase files (D3), so fold the case before the
  // charCode math, which is anchored to lowercase 'a'.
  const col = notation.toLowerCase().charCodeAt(0) - 97;
  const row = 8 - parseInt(notation[1], 10);
  if (col < 0 || col > 7 || row < 0 || row > 7 || isNaN(row)) return null;
  return { row, col };
};

const parseMoveHistory = (text: string): Array<{ row: number; col: number }> => {
  const moves: Array<{ row: number; col: number }> = [];
  const cleaned = text.replace(/●/g, '*');
  for (const line of cleaned.split('\n')) {
    if (!line.trim()) continue;
    const match = line.match(MOVE_LINE);
    if (!match) continue;
    const white = parseSquare(match[2]);
    if (white) moves.push(white);
    if (match[4]) {
      const black = parseSquare(match[4]);
      if (black) moves.push(black);
    }
  }
  return moves;
};

/**
 * Replays a move history from the empty board so a game can be pasted straight
 * in. The board that comes back is the position after the last pasted move, so
 * trimming lines off the end of the paste rewinds the game.
 */
const replayMoveHistory = (
  moves: Array<{ row: number; col: number }>
): { board: (Cell | null)[][]; nextPlayer: 'white' | 'black' } | null => {
  let board = copyBoard(INITIAL_BOARD);
  let player: 'white' | 'black' = 'white';

  for (const { row, col } of moves) {
    if (board[row][col] !== null) return null;

    const next = copyBoard(board);
    next[row][col] = { color: player, isYugo: false };
    const yugos = checkForYugos(next, row, col, player);
    processYugos(next, yugos, row, col);
    if (yugos.length > 0) {
      next[row][col] = { color: player, isYugo: true };
    }
    board = next;

    const opponent: 'white' | 'black' = player === 'white' ? 'black' : 'white';
    if (checkForIgo(board, row, col, player) || !hasLegalMoves(board, opponent)) {
      return { board, nextPlayer: opponent };
    }
    player = opponent;
  }

  return { board, nextPlayer: player };
};

const AdminPuzzleCreator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const [editingPuzzleId, setEditingPuzzleId] = useState<number | null>(null);
  
  // Puzzle list state
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [puzzleError, setPuzzleError] = useState<string | null>(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | 'expert' | 'insane'>('medium');
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Store board state from Sandbox
  const [currentBoard, setCurrentBoard] = useState<(Cell | null)[][] | null>(null);
  const [currentStartingPlayer, setCurrentStartingPlayer] = useState<'white' | 'black'>('white');
  const [currentSolutionMoves, setCurrentSolutionMoves] = useState<SolutionMove[]>([]);
  
  // Solution recording state
  const [isRecordingSolution, setIsRecordingSolution] = useState(false);
  const [isShowingSolution, setIsShowingSolution] = useState(false);
  const [solutionMoveIndex, setSolutionMoveIndex] = useState(0);
  
  // Import position state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');

  // Forced-Igo verification state
  const [verifyMinutes, setVerifyMinutes] = useState(3);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<IgoPuzzleVerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifiedSignature, setVerifiedSignature] = useState<string | null>(null);

  // Hard-puzzle construction state
  const [constructing, setConstructing] = useState(false);
  const [constructGames, setConstructGames] = useState(40);
  const [constructKeep, setConstructKeep] = useState(5);
  const [constructMinScore, setConstructMinScore] = useState(55);
  const [constructProgress, setConstructProgress] = useState<PuzzleConstructProgress | null>(null);
  const [constructCandidates, setConstructCandidates] = useState<ConstructedPuzzleCandidate[]>([]);
  const [constructError, setConstructError] = useState<string | null>(null);
  const [sandboxNonce, setSandboxNonce] = useState(0);

  const currentSignature = useMemo(
    () => positionSignature(currentBoard, currentStartingPlayer),
    [currentBoard, currentStartingPlayer]
  );
  const verificationIsStale = verifiedSignature !== null && verifiedSignature !== currentSignature;

  const handleAnalyzePosition = useCallback(async () => {
    if (!currentBoard) {
      setVerifyError('Set up a position on the board first.');
      return;
    }

    setVerifying(true);
    setVerifyError(null);
    setVerifyResult(null);
    setVerifiedSignature(null);

    const signature = positionSignature(currentBoard, currentStartingPlayer);
    const { data, error } = await AdminApiService.verifyIgoPuzzle({
      board: currentBoard,
      toMove: currentStartingPlayer,
      maxK: 5,
      timeLimitMs: Math.round(verifyMinutes * 60 * 1000),
    });

    if (error || !data) {
      setVerifyError(error || 'Analysis failed.');
    } else {
      setVerifyResult(data);
      setVerifiedSignature(signature);
    }
    setVerifying(false);
  }, [currentBoard, currentStartingPlayer, verifyMinutes]);

  /**
   * Adopt a proven line as the puzzle's solution. Any extra square that also
   * completes the Igo rides along on the final move as an alternate, so the
   * player is not told they are wrong for closing the line at the other end.
   */
  const handleUseVerifiedSolution = useCallback((solutionIndex = 0) => {
    if (!verifyResult) return;
    const sol =
      verifyResult.solutions && verifyResult.solutions[solutionIndex]
        ? verifyResult.solutions[solutionIndex]
        : null;
    const line = sol ? sol.line : verifyResult.solutionLine;
    const finals = sol ? sol.finalMoves : verifyResult.acceptedFinalMoves;
    if (!line || !line.length) return;

    const lastIndex = line.length - 1;
    const moves: SolutionMove[] = line.map((move, index) => {
      if (index !== lastIndex) {
        return { row: move.row, col: move.col, player: move.player };
      }
      const alternates = (finals || []).filter(
        (alt) => !(alt.row === move.row && alt.col === move.col)
      );
      return alternates.length > 0
        ? { row: move.row, col: move.col, player: move.player, alternates }
        : { row: move.row, col: move.col, player: move.player };
    });

    setCurrentSolutionMoves(moves);
    setIsRecordingSolution(false);
    setIsShowingSolution(false);
    setSolutionMoveIndex(0);
    if (verifyResult.suggestedDifficulty) {
      setDifficulty(verifyResult.suggestedDifficulty);
    }
    setError(null);
  }, [verifyResult]);

  const handleConstructHardPuzzles = useCallback(async () => {
    setConstructing(true);
    setConstructError(null);
    setConstructProgress({
      gamesReviewed: 0,
      gamesTotal: constructGames,
      constructed: 0,
      seedsFound: 0,
      message: 'Starting…',
    });
    setConstructCandidates([]);

    const { data, error: apiError } = await AdminApiService.constructHardPuzzles(
      {
        games: constructGames,
        keep: constructKeep,
        minScore: constructMinScore,
        minK: 3,
        maxK: 5,
        mutations: 8,
        timeLimitMs: 20000,
      },
      (progress) => setConstructProgress(progress)
    );

    if (apiError || !data) {
      setConstructError(apiError || 'Construction failed.');
      setConstructing(false);
      return;
    }

    setConstructCandidates(data.candidates || []);
    setConstructProgress({
      gamesReviewed: data.stats.gamesReviewed,
      gamesTotal: data.stats.games,
      constructed: data.candidates.length,
      seedsFound: data.stats.seedsFound,
      message:
        data.candidates.length > 0
          ? `Done — ${data.candidates.length} hard puzzle(s) ready to load.`
          : 'Done — no puzzles met the hardness bar. Try more games or a lower min score.',
    });
    setConstructing(false);
  }, [constructGames, constructKeep, constructMinScore]);

  const handleLoadConstructedCandidate = useCallback((candidate: ConstructedPuzzleCandidate) => {
    const board = copyBoard(candidate.board);
    const line = candidate.solutionLine || [];
    const lastIndex = line.length - 1;
    const finals = candidate.acceptedFinalMoves || [];
    const moves: SolutionMove[] = line.map((move, index) => {
      if (index !== lastIndex) {
        return { row: move.row, col: move.col, player: move.player };
      }
      const alternates = finals.filter(
        (alt) => !(alt.row === move.row && alt.col === move.col)
      );
      return alternates.length > 0
        ? { row: move.row, col: move.col, player: move.player, alternates }
        : { row: move.row, col: move.col, player: move.player };
    });

    setEditingPuzzleId(null);
    setCurrentBoard(board);
    setCurrentStartingPlayer(candidate.toMove);
    setCurrentSolutionMoves(moves);
    setIsRecordingSolution(false);
    setIsShowingSolution(false);
    setSolutionMoveIndex(0);
    setDifficulty(candidate.suggestedDifficulty || 'expert');
    setTitle(
      `${candidate.toMove === 'white' ? 'White' : 'Black'} to move - Igo in ${candidate.minimalK}`
    );
    setDescription(
      `${candidate.toMove === 'white' ? 'White' : 'Black'} to move. ` +
      `Forced Igo in ${candidate.minimalK}. ` +
      `Hardness ${candidate.hardness.score}` +
      (candidate.hardness.quietKey ? ' (quiet key)' : '') +
      '.'
    );
    setVerifyResult(null);
    setVerifyError(null);
    setVerifiedSignature(null);
    setError(null);
    setSuccess(false);
    setSandboxNonce((n) => n + 1);
  }, []);

  // Handler to capture board state from Sandbox
  const handleBoardStateUpdate = useCallback((board: (Cell | null)[][], startingPlayer: 'white' | 'black') => {
    setCurrentBoard(board);
    setCurrentStartingPlayer(startingPlayer);
  }, []);

  // Custom handler for Sandbox's onStartGame (not used for puzzle creation, but required)
  const handleSandboxStartGame = useCallback((board: (Cell | null)[][], startingPlayer: 'white' | 'black') => {
    setCurrentBoard(board);
    setCurrentStartingPlayer(startingPlayer);
  }, []);

  // Handler for recorded moves during solution recording
  const handleMoveRecorded = useCallback((move: { row: number; col: number; player: 'white' | 'black' }) => {
    if (isRecordingSolution) {
      setCurrentSolutionMoves(prev => [...prev, move]);
    }
  }, [isRecordingSolution]);

  // Toggle solution recording
  const handleToggleRecording = useCallback(() => {
    if (!isRecordingSolution) {
      // Starting recording - clear existing solution and start fresh
      setCurrentSolutionMoves([]);
      setIsRecordingSolution(true);
      setError(null);
    } else {
      // Stopping recording
      setIsRecordingSolution(false);
    }
  }, [isRecordingSolution]);

  // Clear solution
  const handleClearSolution = useCallback(() => {
    if (window.confirm('Clear the recorded solution?')) {
      setCurrentSolutionMoves([]);
      setIsRecordingSolution(false);
      setIsShowingSolution(false);
      setSolutionMoveIndex(0);
    }
  }, []);

  // Remove a specific move from solution
  const handleRemoveMove = useCallback((index: number) => {
    setCurrentSolutionMoves(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Parse position string
  const parsePositionString = useCallback((text: string): { board: (Cell | null)[][], startingPlayer: 'white' | 'black' } | null => {
    const trimmed = text.trim();
    if (!/(WM|WY|BM|BY):/i.test(trimmed)) {
      return null; // Doesn't look like a position string
    }

    const board: (Cell | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
    let startingPlayer: 'white' | 'black' = 'white';

    const parts = trimmed.split(';');
    for (const part of parts) {
      if (/^START:/i.test(part.trim())) {
        const player = part.split(':')[1].trim().toLowerCase();
        if (player === 'white' || player === 'black') {
          startingPlayer = player;
        }
        continue;
      }

      const [rawType, squares] = part.split(':');
      if (!squares) continue;
      const type = rawType.trim().toUpperCase();

      const squareList = squares.split(',');
      for (const square of squareList) {
        const parsedSquare = parseSquare(square.trim());
        if (!parsedSquare) continue;
        const { row, col } = parsedSquare;
        if (type === 'WM') {
          board[row][col] = { color: 'white', isYugo: false };
        } else if (type === 'WY') {
          board[row][col] = { color: 'white', isYugo: true };
        } else if (type === 'BM') {
          board[row][col] = { color: 'black', isYugo: false };
        } else if (type === 'BY') {
          board[row][col] = { color: 'black', isYugo: true };
        }
      }
    }

    return { board, startingPlayer };
  }, []);

  // Handle importing position
  const handleImportPosition = useCallback(() => {
    if (!importText.trim()) {
      setError('Please paste a position string first');
      return;
    }

    const text = importText.trim();
    const parsed = parsePositionString(text);

    let board: (Cell | null)[][];
    let startingPlayer: 'white' | 'black';

    if (parsed) {
      if (parsed.board.every((rowCells) => rowCells.every((cell) => cell === null))) {
        setError('That position string contained no pieces. Expected: WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white');
        return;
      }
      board = parsed.board;
      startingPlayer = parsed.startingPlayer;
    } else {
      const moves = parseMoveHistory(text);
      if (moves.length === 0) {
        setError('Could not read that paste. Use a position string (WM:a1,b2;BM:c3;START:white) or a move history copied from a game replay (1. d3 e4).');
        return;
      }
      const replayed = replayMoveHistory(moves);
      if (!replayed) {
        setError('That move history could not be replayed - one of the moves lands on an occupied square.');
        return;
      }
      board = replayed.board;
      startingPlayer = replayed.nextPlayer;
    }
    
    // Set the board and starting player
    setCurrentBoard(copyBoard(board));
    setCurrentStartingPlayer(startingPlayer);
    
    // Clear solution moves when importing a new position
    setCurrentSolutionMoves([]);
    setIsRecordingSolution(false);
    
    // Close modal and clear import text
    setShowImportModal(false);
    setImportText('');
    setError(null);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  }, [importText, parsePositionString]);

  // Load puzzles list
  const loadPuzzles = useCallback(async () => {
    setLoadingPuzzles(true);
    setPuzzleError(null);
    try {
      const result = await PuzzleService.getPuzzles({
        include_private: true,
        limit: 500,
        orderBy: 'catalog_number',
        orderDir: 'asc',
      });
      if (result.success && result.data) {
        setPuzzles(result.data.puzzles);
      } else {
        setPuzzleError(result.error || 'Failed to load puzzles');
      }
    } catch (err: any) {
      setPuzzleError(err.message || 'Failed to load puzzles');
    } finally {
      setLoadingPuzzles(false);
    }
  }, []);

  // Load puzzles when switching to manage tab
  useEffect(() => {
    if (activeTab === 'manage') {
      loadPuzzles();
    }
  }, [activeTab, loadPuzzles]);

  // Load puzzle for editing
  const handleEditPuzzle = useCallback(async (puzzleId: number) => {
    setLoadingPuzzles(true);
    setPuzzleError(null);
    try {
      const result = await PuzzleService.getPuzzleById(puzzleId);
      if (result.success && result.puzzle) {
        const puzzle = result.puzzle;
        setEditingPuzzleId(puzzleId);
        setTitle(puzzle.title);
        setDescription(puzzle.description || '');
        setDifficulty(puzzle.difficulty);
        setIsPublic(puzzle.is_public);
        // Set board and starting player - Sandbox will pick these up via props
        setCurrentBoard(copyBoard(puzzle.initial_board));
        setCurrentStartingPlayer(puzzle.starting_player);
        setCurrentSolutionMoves(puzzle.solution_moves || []);
        setIsRecordingSolution(false);
        setActiveTab('create');
      } else {
        setPuzzleError(result.error || 'Failed to load puzzle');
      }
    } catch (err: any) {
      setPuzzleError(err.message || 'Failed to load puzzle');
    } finally {
      setLoadingPuzzles(false);
    }
  }, []);

  // Delete puzzle
  const handleDeletePuzzle = useCallback(async (puzzleId: number) => {
    if (!window.confirm('Are you sure you want to delete this puzzle? This action cannot be undone.')) {
      return;
    }
    
    setLoadingPuzzles(true);
    setPuzzleError(null);
    try {
      const result = await PuzzleService.deletePuzzle(puzzleId);
      if (result.success) {
        await loadPuzzles();
      } else {
        setPuzzleError(result.error || 'Failed to delete puzzle');
      }
    } catch (err: any) {
      setPuzzleError(err.message || 'Failed to delete puzzle');
    } finally {
      setLoadingPuzzles(false);
    }
  }, [loadPuzzles]);

  const handleSavePuzzle = async () => {
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!currentBoard) {
      setError('Please set up a board position first');
      return;
    }

    // Check if board has at least one piece
    let hasPiece = false;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (currentBoard[row][col] !== null) {
          hasPiece = true;
          break;
        }
      }
      if (hasPiece) break;
    }

    if (!hasPiece) {
      setError('Please place at least one piece on the board');
      return;
    }

    // Check if admin token exists (admins can create puzzles with admin token)
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('authToken');
    
    if (!adminToken && !userToken) {
      setError('You must be logged in to create puzzles.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let result;
      if (editingPuzzleId) {
        // Update existing puzzle
        result = await PuzzleService.updatePuzzle(editingPuzzleId, {
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
          isPublic,
        });
      } else {
        // Create new puzzle
        result = await PuzzleService.createPuzzle({
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
          isPublic,
        });
      }

      if (result.success) {
        setSuccess(true);
        // Reset form
        setTitle('');
        setDescription('');
        setDifficulty('medium');
        setIsPublic(true);
        setCurrentBoard(null);
        setCurrentStartingPlayer('white');
        setCurrentSolutionMoves([]);
        setIsRecordingSolution(false);
        setEditingPuzzleId(null);
        
        // Reload puzzles if on manage tab
        if (activeTab === 'manage') {
          await loadPuzzles();
        }
        
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError(result.error || `Failed to ${editingPuzzleId ? 'update' : 'create'} puzzle`);
      }
    } catch (err: any) {
      setError(err.message || `An error occurred while ${editingPuzzleId ? 'updating' : 'creating'} the puzzle`);
      console.error('Error saving puzzle:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-puzzle-creator">
      <div className="admin-puzzle-creator-tabs">
        <button
          className={`admin-puzzle-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          {editingPuzzleId ? `Edit Puzzle #${editingPuzzleId}` : 'Create Puzzle'}
        </button>
        <button
          className={`admin-puzzle-tab ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          Manage Puzzles
        </button>
      </div>

      {activeTab === 'manage' ? (
        <div className="admin-puzzle-manager">
          {loadingPuzzles ? (
            <div className="puzzle-loading">Loading puzzles...</div>
          ) : puzzleError ? (
            <div className="puzzle-error">{puzzleError}</div>
          ) : puzzles.length === 0 ? (
            <div className="puzzle-empty">No puzzles found.</div>
          ) : (
            <div className="puzzle-list">
              {puzzles.map((puzzle) => (
                <div key={puzzle.id} className="puzzle-item">
                  <div className="puzzle-item-info">
                    <h3>{formatPuzzleDisplayTitle(puzzle)}</h3>
                    <div className="puzzle-item-meta">
                      <span className={`difficulty difficulty-${puzzle.difficulty}`}>
                        {puzzle.difficulty}
                      </span>
                      <span className={`visibility ${puzzle.is_public ? 'public' : 'private'}`}>
                        {puzzle.is_public ? 'Public' : 'Private'}
                      </span>
                      <span className="creator">By {puzzle.creator_username}</span>
                      <span className="moves">
                        {puzzle.solution_moves ? puzzle.solution_moves.length : 0} moves
                      </span>
                    </div>
                    {puzzle.description && (
                      <p className="puzzle-item-description">{puzzle.description}</p>
                    )}
                  </div>
                  <div className="puzzle-item-actions">
                    <button
                      onClick={() => handleEditPuzzle(puzzle.id)}
                      className="edit-puzzle-btn"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePuzzle(puzzle.id)}
                      className="delete-puzzle-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="admin-puzzle-creator-content">
        <div className="admin-puzzle-creator-form">
          <div className="form-group">
            <label htmlFor="puzzle-title">Title *</label>
            <input
              id="puzzle-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter puzzle title"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="puzzle-description">Description</label>
            <textarea
              id="puzzle-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter puzzle description (optional)"
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label htmlFor="puzzle-difficulty">Difficulty *</label>
            <select
              id="puzzle-difficulty"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard' | 'expert' | 'insane')}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="expert">Expert</option>
              <option value="insane">Insane</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make puzzle public
            </label>
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          {success && (
            <div className="form-success">
              Puzzle created successfully!
            </div>
          )}

          <div className="form-group">
            <label>Import Position</label>
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '8px' }}
            >
              📥 Import Position String
            </button>
          </div>

          <div className="form-group admin-igo-verify-panel admin-construct-panel">
            <label>Construct hard puzzles</label>
            <p className="admin-igo-verify-hint">
              Searches archived games for unique forced Igo lines, steps earlier to hide the
              obvious attack, then keeps only positions that score as hard/expert (quiet key,
              decoys, board-wide fight). Load a candidate into the editor, then save.
            </p>

            <div className="admin-igo-verify-controls">
              <label htmlFor="construct-games">
                Games
                <select
                  id="construct-games"
                  value={constructGames}
                  onChange={(e) => setConstructGames(Number(e.target.value))}
                  disabled={constructing}
                >
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={80}>80</option>
                </select>
              </label>
              <label htmlFor="construct-keep">
                Keep
                <select
                  id="construct-keep"
                  value={constructKeep}
                  onChange={(e) => setConstructKeep(Number(e.target.value))}
                  disabled={constructing}
                >
                  <option value={3}>3</option>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                </select>
              </label>
              <label htmlFor="construct-score">
                Min score
                <select
                  id="construct-score"
                  value={constructMinScore}
                  onChange={(e) => setConstructMinScore(Number(e.target.value))}
                  disabled={constructing}
                >
                  <option value={40}>40 (hard)</option>
                  <option value={55}>55 (expert)</option>
                  <option value={70}>70 (very hard)</option>
                </select>
              </label>
            </div>

            <button
              type="button"
              className="btn btn-secondary admin-igo-verify-btn"
              onClick={handleConstructHardPuzzles}
              disabled={constructing}
            >
              {constructing ? 'Building…' : 'Build hard puzzles'}
            </button>

            {constructing && constructProgress && (
              <p className="admin-igo-verify-hint">{constructProgress.message}</p>
            )}

            {!constructing && constructProgress && constructCandidates.length === 0 && !constructError && (
              <p className="admin-igo-verify-hint">{constructProgress.message}</p>
            )}

            {constructError && <div className="admin-igo-verify-error">{constructError}</div>}

            {constructCandidates.length > 0 && (
              <div className="admin-construct-list">
                {constructCandidates.map((candidate, index) => (
                  <div key={`${candidate.position}-${index}`} className="admin-construct-item">
                    <div className="admin-construct-item-main">
                      <strong>
                        #{index + 1} Igo in {candidate.minimalK} · score {candidate.hardness.score} ·{' '}
                        {candidate.suggestedDifficulty}
                      </strong>
                      <span>
                        {candidate.hardness.quietKey ? 'quiet key' : 'loud key'} · decoys{' '}
                        {candidate.metrics.decoyCount} · span{' '}
                        {candidate.hardness.span.rowSpan}×{candidate.hardness.span.colSpan}
                      </span>
                      <span className="admin-construct-line">{candidate.solutionText}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleLoadConstructedCandidate(candidate)}
                      disabled={constructing}
                    >
                      Load into editor
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group admin-igo-verify-panel">
            <label>Check for a Forced Igo</label>
            <p className="admin-igo-verify-hint">
              Looks for an Igo the side to move can force in 2, 3, 4 or 5 moves against every defence,
              with only one way to do it. An Igo available immediately is not a puzzle, and neither
              is one with more than one answer.
            </p>

            <div className="admin-igo-verify-controls">
              <label htmlFor="igo-verify-minutes">
                Give up after
                <select
                  id="igo-verify-minutes"
                  value={verifyMinutes}
                  onChange={(e) => setVerifyMinutes(Number(e.target.value))}
                  disabled={verifying}
                >
                  <option value={1}>1 minute</option>
                  <option value={3}>3 minutes</option>
                  <option value={7}>7 minutes</option>
                  <option value={15}>15 minutes</option>
                </select>
              </label>
            </div>

            <button
              type="button"
              className="btn btn-secondary admin-igo-verify-btn"
              onClick={handleAnalyzePosition}
              disabled={verifying || !currentBoard}
            >
              {verifying ? 'Analysing…' : 'Analyse position'}
            </button>

            {verifying && (
              <p className="admin-igo-verify-hint">
                Working through every defence. Hard positions can take several minutes.
              </p>
            )}

            {verifyError && <div className="admin-igo-verify-error">{verifyError}</div>}

            {verifyResult && verificationIsStale && (
              <div className="admin-igo-verify-stale">
                The board changed after this analysis. Analyse again before saving.
              </div>
            )}

            {verifyResult && !verificationIsStale && (
              <div
                className={`admin-igo-verify-result ${verifyResult.publishable ? 'is-good' : 'is-bad'}`}
              >
                <div className="admin-igo-verify-verdict">{verifyResult.verdict}</div>

                <ul className="admin-igo-verify-facts">
                  <li>
                    Attacker: {verifyResult.attacker}
                    {verifyResult.minimalK !== null && ` — forces an Igo in ${verifyResult.minimalK}`}
                  </li>
                  {verifyResult.moreThanTwoSolutions ? (
                    <li>
                      More than 2 solutions
                      {verifyResult.ambiguousMoves.length > 0
                        ? ` — competing moves include ${describeSquares(verifyResult.ambiguousMoves)}`
                        : verifyResult.winningRootMoves.length > 0
                          ? ` — winning first moves include ${describeSquares(verifyResult.winningRootMoves)}`
                          : ''}
                    </li>
                  ) : (
                    <>
                      {verifyResult.winningRootMoves.length > 0 && (
                        <li>
                          Winning first move
                          {verifyResult.winningRootMoves.length > 1 ? 's' : ''}:{' '}
                          {describeSquares(verifyResult.winningRootMoves)}
                        </li>
                      )}
                      {(verifyResult.solutions && verifyResult.solutions.length > 0
                        ? verifyResult.solutions
                        : verifyResult.solutionLine.length > 0
                          ? [
                              {
                                firstMove: {
                                  row: verifyResult.solutionLine[0].row,
                                  col: verifyResult.solutionLine[0].col,
                                },
                                line: verifyResult.solutionLine,
                                finalMoves: verifyResult.acceptedFinalMoves,
                              },
                            ]
                          : []
                      ).map((sol, solIndex) => (
                        <li key={`sol-${solIndex}`} className="admin-igo-verify-solution">
                          <div>
                            {verifyResult.solutions && verifyResult.solutions.length > 1
                              ? `Solution ${solIndex + 1}`
                              : 'Line'}
                          </div>
                          <pre className="admin-igo-verify-score">{formatSolutionNotation(sol.line)}</pre>
                          {sol.finalMoves.length > 1 && (
                            <div className="admin-igo-verify-alternates">
                              Last move also wins at {describeSquares(sol.finalMoves)}
                            </div>
                          )}
                        </li>
                      ))}
                    </>
                  )}
                  <li>
                    {verifyResult.metrics.decoyCount} move
                    {verifyResult.metrics.decoyCount === 1 ? '' : 's'} look promising but lose
                    {verifyResult.suggestedDifficulty
                      ? ` — suggested difficulty ${verifyResult.suggestedDifficulty}`
                      : ''}
                  </li>
                  <li className="admin-igo-verify-meta">
                    Searched {verifyResult.totalNodesVisited.toLocaleString()} positions in{' '}
                    {(verifyResult.elapsedMs / 1000).toFixed(1)}s · rules {verifyResult.rulesFingerprint}
                  </li>
                </ul>

                {verifyResult.publishable ? (
                  <button
                    type="button"
                    className="btn btn-primary admin-igo-verify-use-btn"
                    onClick={() => handleUseVerifiedSolution(0)}
                  >
                    Use this line as the solution
                  </button>
                ) : verifyResult.solutions &&
                  verifyResult.solutions.length > 0 &&
                  !verifyResult.trivial &&
                  !verifyResult.moreThanTwoSolutions ? (
                  <div className="admin-igo-verify-use-group">
                    {verifyResult.solutions.map((sol, solIndex) => (
                      <button
                        key={`use-${solIndex}`}
                        type="button"
                        className="btn btn-secondary admin-igo-verify-use-btn"
                        onClick={() => handleUseVerifiedSolution(solIndex)}
                      >
                        Use solution {solIndex + 1} (
                        {squareName(sol.firstMove.row, sol.firstMove.col)})
                      </button>
                    ))}
                    <p className="admin-igo-verify-hint">
                      {verifyResult.solutions.length > 1
                        ? 'Two forcing plans — publishing usually wants a unique answer, but you can adopt either line.'
                        : 'This line is forced but not a unique published-style plan; you can still adopt it.'}
                    </p>
                  </div>
                ) : (
                  <p className="admin-igo-verify-hint">
                    {verifyResult.trivial
                      ? 'The Igo is available right now, so there is nothing to work out.'
                      : verifyResult.minimalK === null
                        ? 'Adjust the position until one side can force the Igo.'
                        : verifyResult.moreThanTwoSolutions
                          ? 'Too many answers for a clean puzzle. Shut down all but one of the competing wins and analyse again.'
                          : 'Adjust the position until only one first move wins.'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="form-group solution-controls">
            <label>Solution Moves</label>
            <div className="solution-buttons">
              <button
                type="button"
                onClick={handleToggleRecording}
                className={`record-solution-btn ${isRecordingSolution ? 'recording' : ''}`}
              >
                {isRecordingSolution ? '⏹ Stop Recording' : '⏺ Record Solution'}
              </button>
              <button
                type="button"
                onClick={handleClearSolution}
                disabled={currentSolutionMoves.length === 0}
                className="clear-solution-btn"
              >
                Clear
              </button>
            </div>
            {currentSolutionMoves.length > 0 && (
              <div className="solution-moves-list">
                <div className="solution-moves-header">
                  {currentSolutionMoves.length} move{currentSolutionMoves.length !== 1 ? 's' : ''} recorded
                </div>
                <div className="solution-moves-items">
                  {currentSolutionMoves.map((move, index) => {
                    const colLetter = String.fromCharCode(97 + move.col);
                    const rowNumber = 8 - move.row;
                    return (
                      <div key={index} className="solution-move-item">
                        <span className="move-number">{index + 1}.</span>
                        <span className="move-player">{move.player === 'white' ? 'White' : 'Black'}</span>
                        <span className="move-position">{colLetter}{rowNumber}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveMove(index)}
                          className="remove-move-btn"
                          title="Remove move"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {isRecordingSolution && (
              <div className="recording-indicator">
                <span className="recording-dot"></span>
                Recording solution moves...
              </div>
            )}
          </div>

          <div className="form-actions">
            {editingPuzzleId && (
              <button
                onClick={() => {
                  setEditingPuzzleId(null);
                  setTitle('');
                  setDescription('');
                  setDifficulty('medium');
                  setIsPublic(true);
                  setCurrentBoard(null);
                  setCurrentStartingPlayer('white');
                  setCurrentSolutionMoves([]);
                  setIsRecordingSolution(false);
                }}
                className="cancel-edit-btn"
              >
                Cancel Edit
              </button>
            )}
            <button
              onClick={handleSavePuzzle}
              disabled={saving || !title.trim()}
              className="save-puzzle-btn"
            >
              {saving ? 'Saving...' : editingPuzzleId ? 'Update Puzzle' : 'Save Puzzle'}
            </button>
          </div>

        </div>

        <div className="admin-puzzle-creator-sandbox">
          <Sandbox
            key={sandboxNonce}
            onBoardStateChange={handleBoardStateUpdate}
            onStartGame={handleSandboxStartGame}
            onBack={() => {}}
            onMoveRecorded={handleMoveRecorded}
            isRecordingSolution={isRecordingSolution}
            initialBoard={currentBoard || undefined}
            initialStartingPlayer={currentStartingPlayer}
            showEngineToggle={true}
          />
        </div>
      </div>
      )}

      {/* Import Position Modal */}
      {showImportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowImportModal(false);
            setImportText('');
            setError(null);
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              color: '#000'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '15px' }}>Import Position</h2>
            <p style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
              Paste either a position string:<br />
              <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>
                WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white
              </code>
              <br />
              …or a move history copied from a game replay (<code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>1. D3 E4</code>).
              A move history loads the position after its last move, so delete lines from the end to rewind the game.
            </p>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste a position string or a copied move history here..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '10px',
                fontSize: '13px',
                fontFamily: 'monospace',
                borderRadius: '4px',
                border: '1px solid #ccc',
                marginBottom: '15px',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowImportModal(false);
                  setImportText('');
                  setError(null);
                }
              }}
            />
            {error && (
              <div className="form-error" style={{ marginBottom: '15px' }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportText('');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImportPosition}
                disabled={!importText.trim()}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPuzzleCreator;

